/**
 * `/scores/player`, keyed on the search params a roster row or a lineup row
 * already knows: playerId, name, teamId, teamName, teamAbbreviation,
 * jerseyNumber, position, injuryStatus, plus tab.
 *
 * The file is named `scores_.player` so this route stays out of `/scores`'s
 * tree, which renders no Outlet. Same reason as `settings_.sessions`.
 *
 * The URL names the player. When it also names the team,
 * usePlayerProfileData reads sports-api /team/roster-get and /team/injuries-get
 * to fill Info, status and the headshot (ISSUE-1164). usePlayerSeasonData reads
 * /player/season-stats-get and /event/player-game-log-get for Stats and Game log
 * (ISSUE-1188).
 */
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { PlayerProfilePane } from "../features/playerProfile/PlayerProfilePane";
import { readPlayerSearch } from "../features/playerProfile/playerProfileReader";
import { usePlayerProfileData } from "../features/playerProfile/usePlayerProfileData";
import { usePlayerSeasonData } from "../features/playerProfile/usePlayerSeasonData";
import { validatePlayerSearch } from "../routeState/playerSearch";
import { SectionScreen } from "../shell/SectionScreen";

const PlayerScreen = (): ReactNode => {
  const search = useSearch({ from: "/scores_/player" });
  const navigate = useNavigate({ from: "/scores/player" });

  const view = readPlayerSearch(search);
  const details = usePlayerProfileData(view);
  const season = usePlayerSeasonData({ view, record: details.record });
  const tab = search.tab ?? "info";

  return (
    // The player's name is the page's h1, in the header (Scores UX spec v1.0
    // section 9), so the section draws no generic "Player" heading over it.
    <SectionScreen heading={null}>
      <PlayerProfilePane
        view={view}
        details={details}
        tab={tab}
        season={season}
        onSelectTab={(nextTab) => {
          void navigate({
            search: (prev) => ({ ...prev, tab: nextTab }),
            replace: true,
          });
        }}
      />
    </SectionScreen>
  );
};

export const Route = createFileRoute("/scores_/player")({
  beforeLoad: requireSignedIn,
  validateSearch: validatePlayerSearch,
  component: PlayerScreen,
});
